import type { ReactNode } from 'react';
import { z } from 'zod';

// Referenced only via `z.infer<typeof ...>` below; the value exists to
// derive the type.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const guidelineSectionSchema = z.object({
  type: z.string(),
  paymentDescription: z.custom<ReactNode>(),
  extraDescription: z.custom<ReactNode>(),
  hasSpaceBetween: z.boolean().optional(),
});

export type GuidelineSection = z.infer<typeof guidelineSectionSchema>;

export const guidelineText: GuidelineSection[] = [
  {
    type: 'Regular class',
    paymentDescription: (
      <>
        <div className="mb-6">
          <p>30-minute session: Up to $12</p>
          <ul className="list-disc list-inside pl-4">
            <li>Base pay: $8</li>
            <li>Completion incentive: $4</li>
          </ul>
        </div>
        <div>
          <p>40-minute session: Up to $16</p>
          <ul className="list-disc list-inside pl-4">
            <li>Base pay: $12</li>
            <li>Completion incentive: $4</li>
          </ul>
        </div>
      </>
    ),
    extraDescription: (
      <>
        <p className="font-medium mb-6">Base pay: Teach a class normally</p>
        <p className="font-bold">Completion incentive:</p>
        <ul className="list-disc list-inside pl-4">
          <li>
            <span className="font-bold">Start on time</span>: Zoom or Google Meet started at least 1-2 minutes before
            the scheduled start time. Students are accepted into Zoom on time, at the scheduled class start time.
          </li>
          <li>
            <span className="font-bold">Finish on time</span>: Class is finished as scheduled and not ended early unless
            otherwise notified by the Air Reading team or school staff/parents (ticket submission needed).
          </li>
          <li>
            <span className="font-bold">Feedback on time</span>: Student feedback is provided within 12 hours.
          </li>
          <li>
            <span className="font-bold">Complete normally</span>: Present lesson correctly, use Air Reading or blurred
            meeting background, and no major teacher IT issues.
          </li>
        </ul>

        <p className="font-bold mt-4">Late to class:</p>
        <ul className="list-disc list-inside pl-4">
          <li>
            If Zoom or Google Meet has not started on time, the Air Reading team will look for a substitute immediately.
          </li>
          <li>
            When a substitute joins Zoom and starts teaching, even if you eventually come to class after being late, you
            will not be eligible to teach the class when it's already subbed.
          </li>
          <li>Late to class no-shows may impact your class assignments and result in contract terminations.</li>
        </ul>
      </>
    ),
    hasSpaceBetween: true,
  },
  {
    type: 'Substitution',
    paymentDescription: (
      <>
        <p className="mb-6">Same pay as regular classes.</p>
        <p>Sub within 12 hrs will receive a $5 incentive</p>
      </>
    ),
    extraDescription: <p>Substitute classes.</p>,
  },
  {
    type: 'Trial class',
    paymentDescription: (
      <>
        <p>25 min trial class: up to $12</p>
        <ul className="list-disc list-inside pl-4">
          <li>Base pay: $8</li>
          <li>Enrollment incetive: $4</li>
        </ul>
      </>
    ),
    extraDescription: (
      <>
        <p className="mb-6">Base pay: Complete teaching a trial class normally.</p>
        <p>Enrollment incentive: Student enrolls to the program after the trial class.</p>
      </>
    ),
  },
  {
    type: 'Student no show',
    paymentDescription: (
      <>
        <p className="mb-6">Regular class: base pay + completion incentive</p>
        <p>Trial: $8 base pay</p>
      </>
    ),
    extraDescription: (
      <div className="space-y-6">
        <p>Regular class: Stay the entire class.</p>
        <p>Trial: Stay 15 min.</p>
        <p>Camera can be turned off and stay on mute.</p>
      </div>
    ),
  },
  {
    type: 'Class cancellation in 12 hours',
    paymentDescription: (
      <>
        <p className="mb-6">Regular class: base + completion incentive</p>
        <p>Trial: $8 base pay</p>
      </>
    ),
    extraDescription: (
      <>
        <p className="mb-6">Classes that are canceled within the 12 hours of the class time.</p>
        <p>No need to attend the class.</p>
      </>
    ),
  },
];
